import { clsx } from 'clsx';
import { CSSProperties, ElementType, PropsWithChildren, createElement, forwardRef } from 'react';
import styles from './Text.module.scss';

export interface TitleProps {
  variant: 'title1' | 'title2' | 'title3' | 'title4';
  weight: 'semibold' | 'light';
}

export interface SubtitleProps {
  variant: 'subtitle1' | 'subtitle2';
  weight?: undefined;
}

interface BodyProps {
  variant: 'body1' | 'body2';
  weight: 'bold' | 'semibold' | 'regular' | 'medium';
}

export interface CaptionProps {
  variant: 'caption';
  weight: 'regular' | 'semibold' | 'medium';
}

export interface TinyProps {
  variant: 'tiny';
  weight: 'bold' | 'medium';
}

type StylesProps = TitleProps | SubtitleProps | BodyProps | CaptionProps | TinyProps;

type Props = StylesProps & {
  className?: string;
  element?: ElementType;
  ellipsis?: boolean;
  uppercase?: boolean;
  align?: 'left' | 'center' | 'right';
  secondaryColor?: boolean;
  style?: CSSProperties;
  tabularNums?: boolean;
  dataTestId?: string;
  id?: string;
};

const variantClass = {
  title1: styles.title1,
  title2: styles.title2,
  title3: styles.title3,
  title4: styles.title4,
  subtitle1: styles.subtitle1,
  subtitle2: styles.subtitle2,
  body1: styles.body1,
  body2: styles.body2,
  caption: styles.caption,
  tiny: styles.tiny,
};

const weightClass = {
  bold: styles.bold,
  semibold: styles.semibold,
  medium: styles.medium,
  regular: styles.regular,
  light: styles.light,
};

export type TextProps = PropsWithChildren<Props>;

const Text = forwardRef(
  (
    {
      children,
      className,
      element,
      ellipsis,
      uppercase,
      align = 'left',
      secondaryColor,
      style,
      tabularNums,
      variant,
      weight,
      dataTestId,
      id,
    }: TextProps,
    ref,
  ) => {
    const mergedClassName = clsx(
      variantClass[variant],
      weight ? weightClass[weight] : undefined,
      {
        [styles.secondaryColor!]: secondaryColor,
        [styles.tabularNums!]: tabularNums,
        [styles.ellipsis!]: ellipsis,
        [styles.uppercase!]: uppercase,
        [styles.alignLeft!]: align === 'left',
        [styles.alignCenter!]: align === 'center',
        [styles.alignRight!]: align === 'right',
      },

      className,
    );
    return createElement(
      element || 'span',
      { className: mergedClassName, style, ref, 'data-testid': dataTestId, id },
      children,
    );
  },
);

Text.displayName = 'Text';

export default Text;
